'use client';

import type { ListPopularPolicyDto } from '__generated__/api/auth/types.gen';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { useAuthStore } from '@/stores/AuthStore';

import type {
  PolicyFilterState,
  PolicySortOption,
} from '../../_hooks/usePolicyFilter';
import {
  EMPTY_AI_SUMMARY_MESSAGE,
  EMPTY_SUPPORT_BENEFIT_MESSAGE,
  PAGE_SIZE,
  PREVIEW_SIZE,
} from '../../constants';
import { buildPaginationMeta, formatApplicationPeriod } from '../../utils';
import { PolicyFilter } from './PolicyFilter';
import { PolicyResultList } from './PolicyResultList';
import type {
  PopularPolicyData,
  PopularPolicySource,
} from './PopularPolicies';
import { PopularPolicies } from './PopularPolicies';

const getDdayDisplay = (dday?: string) => {
  const ddayText = dday?.replace('마감 ', '') ?? '상시';
  const ddayMatch = ddayText.match(/D-(\d+)/);
  const isUrgent = Boolean(ddayMatch && Number(ddayMatch[1]) <= 7);

  return { ddayText, isUrgent };
};

const mapPopularPolicy = (
  policy: ListPopularPolicyDto,
  source?: PopularPolicySource,
): PopularPolicyData => ({
  id: policy.metaPolicyIdIdx,
  title: policy.policyName,
  benefit: policy.supportBenefit ?? EMPTY_SUPPORT_BENEFIT_MESSAGE,
  source,
  ...getDdayDisplay(policy.dday),
});

const interleave = <T,>(a: T[], b: T[]): T[] => {
  const merged: T[] = [];
  const max = Math.max(a.length, b.length);

  for (let i = 0; i < max; i++) {
    if (i < a.length) merged.push(a[i]);
    if (i < b.length) merged.push(b[i]);
  }

  return merged;
};

const dedupePopularPolicies = (policies: PopularPolicyData[]) => {
  const seen = new Set<string>();

  return policies.filter((policy) => {
    if (seen.has(policy.id)) return false;
    seen.add(policy.id);

    return true;
  });
};

interface PolicySearchProps {
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  state: PolicyFilterState;
  onToggleTargetCode: (code: string) => void;
  onToggleProvisionCode: (code: string) => void;
  onToggleJobCode: (code: string) => void;
  onToggleAgeCode: (code: string) => void;
  onResetFilters: () => void;
  onSortChange: (sort: PolicySortOption) => void;
  onPageChange: (page: number) => void;
}

export function PolicySearch({
  isFilterOpen,
  onCloseFilter,
  state,
  onToggleTargetCode,
  onToggleProvisionCode,
  onToggleJobCode,
  onToggleAgeCode,
  onResetFilters,
  onSortChange,
  onPageChange,
}: PolicySearchProps) {
  const isLogin = useAuthStore((state) => state.isLogin);

  const { data: policyData, isLoading: isPolicyLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/policies']({
      query: {
        searchKeyword: state.keyword || undefined,
        targetCodeList:
          state.targetCodes.length > 0 ? state.targetCodes : undefined,
        provisionCodeList:
          state.provisionCodes.length > 0 ? state.provisionCodes : undefined,
        jobCodeList: state.jobCodes.length > 0 ? state.jobCodes : undefined,
        descAgeList: state.ageCodes.length > 0 ? state.ageCodes : undefined,
        policySort: state.sort,
        page: state.page,
        size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
    select: (data) => {
      const context = data.context;

      return {
        policies:
          context?.contents?.map((policy) => ({
            id: policy.metaPolicyIdIdx ?? String(policy.id ?? ''),
            contentId: policy.id,
            title: policy.policyName ?? '',
            aiSummary: policy.aiSummary ?? EMPTY_AI_SUMMARY_MESSAGE,
            applicationPeriod: formatApplicationPeriod(
              policy.dueDateStart,
              policy.dueDateEnd,
            ),
            agencyName: policy.agency ?? '',
            agencyLogo: policy.agencyLogo ?? '',
            isBookmarked: Boolean(policy.wishlistYN),
          })) ?? [],
        ...buildPaginationMeta(context?.totalCount ?? 0),
      };
    },
  });

  const { data: peerPolicies = [] } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member/peer-group/policy'](),
    enabled: Boolean(isLogin),
    select: (data) =>
      (data.context?.policyList ?? []).map((policy) =>
        mapPopularPolicy(policy, 'peer'),
      ),
  });

  const { data: neighborPolicies = [] } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member/neighbor/policy'](),
    enabled: Boolean(isLogin),
    select: (data) =>
      (data.context?.policyList ?? []).map((policy) =>
        mapPopularPolicy(policy, 'neighbor'),
      ),
  });

  const { data: anonymousPopularPolicies = [] } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/policy/popular'](),
    enabled: isLogin === false,
    select: (data) =>
      (data.context?.policyList ?? []).map((policy) =>
        mapPopularPolicy(policy),
      ),
  });

  const popularPolicies = dedupePopularPolicies(
    isLogin
      ? interleave(peerPolicies, neighborPolicies)
      : anonymousPopularPolicies,
  ).slice(0, PREVIEW_SIZE);

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      leftAside={
        <PolicyFilter
          isDrawerOpen={isFilterOpen}
          onCloseDrawer={onCloseFilter}
          onApplyDrawer={onCloseFilter}
          targetCodes={state.targetCodes}
          provisionCodes={state.provisionCodes}
          jobCodes={state.jobCodes}
          ageCodes={state.ageCodes}
          onToggleTargetCode={onToggleTargetCode}
          onToggleProvisionCode={onToggleProvisionCode}
          onToggleJobCode={onToggleJobCode}
          onToggleAgeCode={onToggleAgeCode}
          onReset={onResetFilters}
        />
      }
      rightAside={<PopularPolicies policies={popularPolicies} />}
    >
      <PolicyResultList
        policies={policyData?.policies ?? []}
        totalCount={policyData?.totalCount ?? 0}
        sort={state.sort}
        onSortChange={onSortChange}
        page={state.page}
        totalPages={policyData?.totalPages ?? 0}
        onPageChange={onPageChange}
        isLoading={isPolicyLoading}
        emptyMessage="조건에 맞는 정책이 없습니다"
      />
    </DesktopPageShell>
  );
}
